import {Box, Show} from 'platform/foundation';

import {useState} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {tiresInventoryRoutes} from '@dms/routes';
import {DmsDataGrid, TireSetDetail} from '@dms/shared';

import {composePath} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface TireSetsProps {
  vehicleId: string;
}

const PREDEFINED_COLUMNS = [
  'tireManufacturer',
  'tireSeason',
  'tireOrderId',
  'orderSetId',
  'setNumber',
] as const;

export function TireSets(props: TireSetsProps) {
  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {
        vehicleId: props.vehicleId,
      },
    ]);

  const [editTireSetData, setEditTireSetData] = useState<{
    orderId: string;
    setId: string;
    title: string;
    refreshData: () => void;
  } | null>(null);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    getColumnData,
    refreshData,
    rowId,
  }) => {
    const tireManufacturer = getColumnData<{label: string}>('tireManufacturer')?.value?.label;
    const tireSeason = getColumnData<{label: string}>('tireSeason')?.value?.label;
    const orderId = getColumnData<string>('tireOrderId')?.value;
    const orderSetId = Array.isArray(rowId) ? rowId[0] : rowId;
    const setNumber = getColumnData<string>('setNumber')?.value;

    if (actionKey === 'redirectDetail') {
      const titleData = [tireManufacturer, tireSeason].filter(isNotNilOrEmpty);

      if (isNil(orderId) || isNil(orderSetId)) {
        throw new Error('orderId or orderSetId is undefined');
      }

      setEditTireSetData({
        orderId,
        setId: orderSetId,
        title: `${setNumber} - ${titleData.join(', ')}`,
        refreshData,
      });
    }

    if (actionKey === 'redirectTireOrder') {
      window.open(
        composePath(tiresInventoryRoutes.tireOrderDetail, {
          params: {id: orderId},
        }),
        '_blank'
      );
    }
  };

  return (
    <>
      <Box minHeight={50}>
        <DmsDataGrid
          gridCode="tire-sets-service-case-creating"
          autoHeight
          queryModifier={queryModifier}
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
        />
      </Box>

      <Show when={editTireSetData}>
        {(editTireSetData) => (
          <TireSetDetail
            tireOrderId={editTireSetData.orderId}
            tireSetId={editTireSetData.setId}
            onClose={() => setEditTireSetData(null)}
            onEdit={() => {
              editTireSetData.refreshData();
              setEditTireSetData(null);
            }}
          />
        )}
      </Show>
    </>
  );
}
